import { selectDisplayableProperties } from "./line-item-property-display";
import type {
  TrackingPageLineItem,
  TrackingPageOrder,
} from "./tracking-page-order-definition";

export interface BlockPageMeta {
  route: string;
  locale: string;
  currency: string;
  blockId: string;
  blockIndex: number;
}

export interface BuildBlockContextOptions {
  /** Line-item property names allowlisted for display; strips the rest. */
  lineItemPropertyAllowlist: string[];
  page: BlockPageMeta;
  /** The shipment the shopper is viewing; out of range falls back to the first. */
  selectedShipmentIndex?: number;
  /** Ordered items no live shipment carries yet. */
  unfulfilledLineItems?: TrackingPageLineItem[];
}

function blockLineItem(item: TrackingPageLineItem, allowlist: string[]) {
  const properties = selectDisplayableProperties(item.properties, allowlist);
  return {
    ...item,
    properties,
    // By name, so Liquid can read `propertyValues["Monogram"]` directly.
    propertyValues: Object.fromEntries(
      properties.map(({ name, value }) => [name, value]),
    ),
  };
}

/** Never includes customer or shippingAddress; v1 sends vendor JS no PII. */
export function buildBlockContext(
  order: TrackingPageOrder,
  options: BuildBlockContextOptions,
): Record<string, unknown> {
  const allowlist = options.lineItemPropertyAllowlist;
  const shipments = order.shipments.map((shipment) => ({
    ...shipment,
    lineItems: shipment.lineItems.map((item) => blockLineItem(item, allowlist)),
  }));
  const requestedIndex = options.selectedShipmentIndex ?? 0;
  const selectedShipmentIndex = shipments[requestedIndex] ? requestedIndex : 0;

  return {
    v: 1,
    fulfillmentStatus: order.fulfillmentStatus,
    currency: order.currency,
    source: order.source,
    shipments,
    selectedShipmentIndex,
    selectedShipment: shipments[selectedShipmentIndex],
    unfulfilledLineItems: (options.unfulfilledLineItems ?? []).map((item) =>
      blockLineItem(item, allowlist),
    ),
    page: options.page,
  };
}
